import { IAppointmentType, IClient, IEvent, IStaff } from 'model';
import { Status } from 'model/calendar/events';
import { EVENT_STATUS } from 'utils/constants/status';
import moment, { Moment } from 'moment';
const momentTz = require('moment-timezone');

export const isRecurrent = (recurringEvery?: number) => {
  return !!recurringEvery ? 'Scheduled - Recurring' : 'Scheduled - One Time';
};
export const getStatus = (
  status: Status | undefined,
  recurringEvery?: number,
  isPendingConfirmation?: boolean
) => {
  if (status === EVENT_STATUS.canceled || status === EVENT_STATUS.deleted) {
    return status;
  }

  if (isPendingConfirmation) return 'invisible' as Status;

  return status === EVENT_STATUS.scheduled
    ? isRecurrent(recurringEvery)
    : status;
};

export const getAppointmentId = (appointment?: IAppointmentType) =>
  `${appointment?.id}`;

export const getEventNewSubject = (
  event: IEvent,
  newProvider: IStaff,
  client: IClient,
  apptTypesMap: Map<string, any>
) =>
  `${apptTypesMap.get(getAppointmentId(event.appointmentType))?.title} - ${
    client?.name
  } & ${newProvider?.name}`;

export const getAdminEventNewSubject = (
  event: IEvent,
  newProvider: IStaff,
  apptTypesMap: Map<string, any>
) =>
  `${apptTypesMap.get(getAppointmentId(event.appointmentType))?.title} - ${
    newProvider.name
  }`;
export const isSeriesEndingWithin4Weeks = (
  recurringUntil?: Date | Moment | string | null,
  recurringEvery?: number,
  clinicTimezone?: string
): boolean => {
  if (!recurringEvery || !recurringUntil) {
    return false;
  }

  const endDate = clinicTimezone
    ? momentTz(recurringUntil).tz(clinicTimezone)
    : moment(recurringUntil);

  const now = clinicTimezone ? momentTz.utc().tz(clinicTimezone) : moment();

  const fourthWeekSunday = now
    .clone()
    .add(4, 'weeks')
    .endOf('week') // Gets Sunday (end of week)
    .hour(23)
    .minute(59)
    .second(59);

  return endDate.isValid() && endDate.isSameOrBefore(fourthWeekSunday);
};
// A recurring series can be confirmed but not yet started — the provider's
// standing week should show the slot as taken, flagged with when it begins.
// Mirrors isSeriesEndingWithin4Weeks, over a 6 week look-ahead.
export const SERIES_UPCOMING_WEEKS = 6;

export const isSeriesStartingLater = (
  seriesStart?: Date | Moment | string | null,
  clinicTimezone?: string
): boolean => {
  if (!seriesStart) return false;

  const start = clinicTimezone
    ? momentTz(seriesStart).tz(clinicTimezone)
    : moment(seriesStart);
  if (!start.isValid()) return false;

  const now = clinicTimezone ? momentTz.utc().tz(clinicTimezone) : moment();
  const thisWeekEnd = now
    .clone()
    .endOf('week')
    .hour(23)
    .minute(59)
    .second(59);
  const horizon = now
    .clone()
    .add(SERIES_UPCOMING_WEEKS, 'weeks')
    .endOf('week');

  return start.isAfter(thisWeekEnd) && start.isSameOrBefore(horizon);
};

/**
 * Short form used on the provider template bars — "Aug 26". A slot is only a few
 * characters wide, and the year is noise over a six week look-ahead. The planner
 * tooltip keeps its own longer format (see formatSeriesEndDate).
 */
export const formatSeriesShortDate = (
  date?: Date | Moment | string | null,
  clinicTimezone?: string
): string => {
  if (!date) return '';
  const parsed = clinicTimezone
    ? momentTz(date).tz(clinicTimezone)
    : moment(date);
  return parsed.isValid() ? parsed.format('MMM D') : '';
};

export const formatSeriesStartDate = (
  seriesStart?: Date | Moment | string | null,
  clinicTimezone?: string
): string => formatSeriesShortDate(seriesStart, clinicTimezone);

export const formatSeriesEndDate = (
  recurringUntil?: Date | Moment | string | null,
  clinicTimezone?: string
): string => {
  if (!recurringUntil) {
    return '';
  }

  const endDate = clinicTimezone
    ? momentTz(recurringUntil).tz(clinicTimezone)
    : moment(recurringUntil);

  return endDate.isValid() ? endDate.format('MM/DD/YYYY') : '';
};
